/**
 * CashierPage — B1.7 CashierView smart wrapper (Faz 1.3).
 *
 * Architecture (`.cursor/rules/lovable-boundary.mdc`):
 *   - This page owns data fetching (`useCashierSnapshot`), tenant scoping
 *     (`useCurrentBranch`), cart mutations, and payment orchestration.
 *   - The Lovable-delivered presentational composite lives in
 *     `shared/ui/cashier/` and is imported via `CashierView`.
 *   - B1.8 PaymentDialog handles the real provider-backed tender flow
 *     (cash keypad, card + 3DS, gift_card, transfer) so we pass
 *     `externalTenderControl` to suppress the composite's default tender sheet.
 *   - `features/cashier` types share the same shape as the composite types
 *     (structural typing) — a lightweight cast is sufficient until a shared
 *     types package is introduced.
 *
 * @see docs/_context/PETOTA_BUSINESS_LOVABLE_BRIEF_B1_7_CASHIER_VIEW.md
 */
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useCurrentBranch } from '../shared/context/TenantContext';
import { PaymentDialog } from '../components/cashier/PaymentDialog';
import { QuickCustomerModal } from '../components/customers/QuickCustomerModal';
import {
  CashierView,
  trCashierLabels,
  enCashierLabels,
  type CashierSnapshot as LovableCashierSnapshot,
  type CashierCatalogItem as LovableCashierCatalogItem,
  type CashierTender as LovableCashierTender,
} from '../shared/ui/cashier';
import {
  useAddCartItem,
  useCashierSnapshot,
  useCommitTender,
  useRemoveCartItem,
  useUpdateCartQty,
  type CashierTender,
} from '../features/cashier';
import { useCustomerSummaries } from '../features/customers';

export function CashierPage(): JSX.Element {
  const { t, i18n } = useTranslation(['cashier', 'payment', 'common']);
  const navigate = useNavigate();
  const branch = useCurrentBranch();
  const locale: 'tr' | 'en' = i18n.language?.startsWith('tr') ? 'tr' : 'en';
  const labels = locale === 'tr' ? trCashierLabels : enCashierLabels;

  const [paymentOpen, setPaymentOpen] = useState<boolean>(false);
  const [quickCustomerOpen, setQuickCustomerOpen] = useState<boolean>(false);

  const { data, isLoading, isError, refetch } = useCashierSnapshot(
    branch?.branchId,
  );
  const addItem = useAddCartItem(branch?.branchId);
  const updateQty = useUpdateCartQty(branch?.branchId);
  const removeItem = useRemoveCartItem(branch?.branchId);
  const commitTender = useCommitTender(branch?.branchId);
  const { data: customerSummaries } = useCustomerSummaries();

  const cashierCustomers = useMemo(
    () =>
      (customerSummaries ?? []).map((customer) => ({
        id: customer.id,
        name: customer.fullName,
      })),
    [customerSummaries],
  );

  const lovableSnapshot = useMemo<LovableCashierSnapshot | null>(() => {
    if (!data) return null;
    return data as unknown as LovableCashierSnapshot;
  }, [data]);

  const handleAddItem = useCallback(
    (item: LovableCashierCatalogItem, qty: number) => {
      addItem.mutate(
        { itemId: item.id, qty },
        { onError: () => toast.error(t('cashier:states.error.title')) },
      );
    },
    [addItem, t],
  );

  const handleUpdateQty = useCallback(
    (lineId: string, qty: number) => {
      updateQty.mutate({ lineId, qty });
    },
    [updateQty],
  );

  const handleRemoveLine = useCallback(
    (lineId: string) => {
      removeItem.mutate({ lineId });
    },
    [removeItem],
  );

  const handleOpenTender = useCallback(() => {
    if (!data) return;
    if (data.activeOrder.lines.length === 0) return;
    setPaymentOpen(true);
  }, [data]);

  const handleCloseSession = useCallback(() => {
    navigate('/day-close');
  }, [navigate]);

  const handleCommitFromLovable = useCallback(
    async (_tenders: readonly LovableCashierTender[]) => {
      // Suppressed by externalTenderControl — PaymentDialog drives the real flow.
    },
    [],
  );

  const handleConfirmPayment = useCallback(
    (tenders: readonly CashierTender[]) => {
      commitTender.mutate(
        { tenders },
        {
          onSuccess: (result) => {
            toast.success(
              t('payment:dialog.commitSuccess', { receiptId: result.receiptId }),
            );
            setPaymentOpen(false);
          },
          onError: () => toast.error(t('payment:dialog.commitError')),
        },
      );
    },
    [commitTender, t],
  );

  if (!lovableSnapshot) {
    return (
      <CashierView
        snapshot={
          {
            branchId: branch?.branchId ?? '',
            branchName: branch?.branchName ?? '',
            session: {
              id: '',
              openedByStaffId: '',
              openedByName: '',
              openedAt: new Date().toISOString(),
              closedAt: null,
              status: 'open',
              cashFloatMinor: 0,
              ordersCount: 0,
              grossMinor: 0,
              cashCollectedMinor: 0,
            },
            activeOrder: {
              id: '',
              sessionId: '',
              customerId: null,
              customerName: null,
              petId: null,
              petName: null,
              lines: [],
              subtotalMinor: 0,
              orderDiscountMinor: 0,
              taxMinor: 0,
              totalMinor: 0,
              tenders: [],
              status: 'open',
              parkedLabel: null,
              openedAt: new Date().toISOString(),
              closedAt: null,
            },
            parkedOrders: [],
            categories: [],
            catalog: [],
            availableTenders: ['cash'],
            updatedAt: new Date().toISOString(),
          } as LovableCashierSnapshot
        }
        labels={labels}
        locale={locale}
        isLoading={isLoading}
        error={isError ? { message: t('cashier:states.error.description') } : null}
        onRetry={() => void refetch()}
        dataTestId="cashier-page"
      />
    );
  }

  return (
    <div className="h-full" data-testid="cashier-page">
      <CashierView
        snapshot={lovableSnapshot}
        labels={labels}
        customers={cashierCustomers}
        locale={locale}
        externalTenderControl
        onAddItem={handleAddItem}
        onUpdateQty={handleUpdateQty}
        onRemoveLineItem={handleRemoveLine}
        onOpenTender={handleOpenTender}
        onCreateCustomer={() => setQuickCustomerOpen(true)}
        onCommitTender={handleCommitFromLovable}
        onCloseSession={handleCloseSession}
      />
      <QuickCustomerModal
        open={quickCustomerOpen}
        onClose={() => setQuickCustomerOpen(false)}
      />
      <PaymentDialog
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        totalMinor={lovableSnapshot.activeOrder.totalMinor}
        currency="TRY"
        orderId={lovableSnapshot.activeOrder.id}
        availableTenders={data?.availableTenders ?? ['cash']}
        isCommitting={commitTender.isPending}
        onConfirm={handleConfirmPayment}
      />
    </div>
  );
}

export default CashierPage;
